import React, { useEffect, useRef } from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import tpmToast, { dismissTpmToast } from '@/components/admin/SlideShow/PresentMode/tpmToast';
import { useModalManager } from '@/components/common/Modal';
import { UnstyledButton } from '@/components/ui/Buttons';
// AIDEV-NOTE: PLAT-16888 — notifyModal state lives on the slideshow store so the StudentsPanel's
// "Manage Students" button can open it from outside this component.
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import Icon from '@/components/ui/Icon';
import useLiveSession from '@/channels/liveSession/useLiveSession';
import NotifyStudentsModal from '@/components/admin/SlideShow/PresentMode/Navigator/NotifyStudentsModal';
import {
  useClassrooms,
} from '@/components/admin/SlideShow/PresentMode/Navigator/NotifyStudentsModal/NotifyStudentsModal.utils';
import cn from '@/utils/cn';
import useFlags from '@/hooks/useFlags';
import styles from '../../Navigator.module.scss';
import ConfirmSessionEndModal from './ConfirmSessionEndModal';
import EndSessionModal from './EndSessionModal';
import SessionConflictModal from './SessionConflictModal';

interface LiveSessionControlProps {
  programId: number;
  stafferId: number;
}

// PLAT-16777 — persistent reconnect-toast id; dismissed on the next successful connect().
const CONNECTION_TOAST_ID = 'ls-connection';

// PLAT-17366 — denylist, not allowlist: activityType is ''/null for unit and custom slideshows,
// which must remain TPM-enabled.
const TPM_HIDDEN_ACTIVITY_TYPES = ['marketing', 'professional_development'];

const LiveSessionControl = ({
  programId,
  stafferId,
}: LiveSessionControlProps) => {
  // PLAT-17326 — suppresses the connection-lost toast during a real page-unload, where the
  // WS `disconnected` fires as a side effect of teardown rather than a real drop.
  const isUnloadingRef = useRef(false);
  const pendingStudentIdsRef = useRef<number[]>([]);
  // PLAT-16777 — connectionState transitions 'connecting' → 'connected' on initial subscribe.
  // Only dismiss the toast after a prior 'disconnected' transition.
  const prevConnectionRef = useRef<string | null>(null);

  useEffect(() => {
    const onUnload = () => { isUnloadingRef.current = true; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  const activeSessionId = useSlideShowContext(state => state.activeSessionId);
  const activityType = useSlideShowContext(state => state.activityType);
  const classroomConflictSessionActive = useSlideShowContext(state => state.classroomConflictSessionActive);
  const closeEndSessionModal = useSlideShowContext(state => state.closeEndSessionModal);
  const closeStoreNotifyModal = useSlideShowContext(state => state.closeNotifyModal);
  const connectedStudentIds = useSlideShowContext(state => state.connectedStudentIds);
  const endSessionModalOpen = useSlideShowContext(state => state.endSessionModalOpen);
  const notifyModalOpen = useSlideShowContext(state => state.notifyModalOpen);
  const openEndSessionModal = useSlideShowContext(state => state.openEndSessionModal);
  const openStoreNotifyModal = useSlideShowContext(state => state.openNotifyModal);
  const setActiveSessionId = useSlideShowContext(state => state.setActiveSessionId);
  const setClassroomConflictSessionActive = useSlideShowContext(state => state.setClassroomConflictSessionActive);
  const setIsSessionConnected = useSlideShowContext(state => state.setIsSessionConnected);
  const setStudentCounts = useSlideShowContext(state => state.setStudentCounts);
  const slideShowId = useSlideShowContext(state => state.slideShowId);
  const parentId = useSlideShowContext(state => state.parentId);
  const parentType = useSlideShowContext(state => state.parentType);
  const confirmSessionEndModal = useModalManager();
  const { teacherPacedMode, usesTeacherPacedMode } = useFlags();
  const conflictModal = useModalManager();
  const notifyModal = {
    close: closeStoreNotifyModal,
    isOpen: notifyModalOpen,
    open: openStoreNotifyModal,
  };

  // Prefetch classrooms so the notify modal has data ready when it opens.
  const { isFetched } = useClassrooms({ programId, stafferId });

  // PLAT-16887 — student_count_changed drives the toolbar's "Presenting to X of Y" UI.
  const {
    connectionState,
    studentCounts,
  } = useLiveSession(activeSessionId);

  useEffect(() => {
    if (!activeSessionId) return;
    setStudentCounts({
      connected: studentCounts.connectedCount,
      connectedIds: studentCounts.connectedIds,
      invitedIds: studentCounts.invitedIds,
    });
  }, [activeSessionId, studentCounts, setStudentCounts]);

  useEffect(() => {
    if (!activeSessionId) {
      prevConnectionRef.current = null;
      return;
    }
    if (connectionState === 'connected') {
      setIsSessionConnected(true);
      if (prevConnectionRef.current === 'disconnected') {
        dismissTpmToast(CONNECTION_TOAST_ID);
      }
    } else if (connectionState === 'disconnected') {
      if (!isUnloadingRef.current) {
        setIsSessionConnected(false);
        tpmToast(I18n.t('live_session_connection_lost_body'), {
          autoClose: false,
          msgHeading: I18n.t('live_session_connection_lost_title'),
          msgType: 'warning',
          toastId: CONNECTION_TOAST_ID,
        });
      }
    }
    prevConnectionRef.current = connectionState;
  }, [activeSessionId, connectionState, setIsSessionConnected]);

  const notifyMutation = useMutation({
    mutationFn: ({ sessionId, studentIds }: { sessionId: string; studentIds: number[] }) => Axios.post(
      Routes.plato_api_notify_live_session_path(sessionId),
      { student_ids: studentIds },
    ),
  });

  const startMutation = useMutation({
    mutationFn: () => Axios.post(
      Routes.plato_api_live_sessions_path(),
      {
        parent_id: parentId,
        parent_type: parentType,
        program_id: programId,
        slide_show_id: slideShowId,
      },
    ),
    onError: (error: any) => {
      if (error?.response?.data?.code === 'classroom_conflict') {
        setClassroomConflictSessionActive(true);
        notifyModal.close();
        conflictModal.open();
      }
    },
    onSuccess: (response) => {
      const sessionId = response.data.data.session_id;
      setActiveSessionId(sessionId);

      // AIDEV-NOTE: After session creation, notify selected students. If notify fails,
      // the modal stays open with an error state so the teacher can retry.
      notifyMutation.mutate(
        { sessionId, studentIds: pendingStudentIdsRef.current },
        {
          onSuccess: (notifyResponse) => {
            const count = notifyResponse.data.data.notified_count;
            tpmToast(I18n.t('students_notified', { count }), {
              msgHeading: I18n.t('session_live_students_notified_title'),
            });
            notifyModal.close();
          },
          onError: () => {
            tpmToast(I18n.t('notify_students_error'), {
              msgHeading: I18n.t('notify_students_error_title'),
              msgType: 'error',
            });
          },
        },
      );
    },
  });

  const endMutation = useMutation({
    mutationFn: () => Axios.delete(
      Routes.plato_api_live_session_path(activeSessionId),
    ),
    onSuccess: () => {
      confirmSessionEndModal.open();
      setActiveSessionId(null);
      closeEndSessionModal();
    },
  });

  const handleStartClick = () => {
    if (classroomConflictSessionActive) {
      conflictModal.open();
    } else {
      notifyModal.open();
    }
  };

  const handleInitialSubmit = (studentIds: number[]) => {
    pendingStudentIdsRef.current = studentIds;
    startMutation.mutate();
  };

  const closeNotifyModal = () => {
    startMutation.reset();
    notifyMutation.reset();
    notifyModal.close();
  };

  const handleRenotifySubmit = (studentIds: number[]) => {
    notifyMutation.mutate(
      { sessionId: activeSessionId!, studentIds },
      {
        onSuccess: (notifyResponse) => {
          const count = notifyResponse.data.data.notified_count;
          tpmToast(I18n.t('students_notified', { count }), {
            msgHeading: I18n.t('students_notified_title'),
          });
          notifyModal.close();
        },
        onError: () => {
          tpmToast(I18n.t('notify_students_error'), {
            msgHeading: I18n.t('notify_students_error_title'),
            msgType: 'error',
          });
        },
      },
    );
  };

  if (!programId) return null;

  if (activeSessionId) {
    return (
      <>
        <UnstyledButton
          aria-label={I18n.t('send_notification')}
          className={cn(styles.settingsMenuRow, teacherPacedMode ? styles.teacherPacedUpdates : styles.legacy)}
          onClick={notifyModal.open}
        >
          <span className={styles.menuIconButton}>
            <Icon icon="Live" size={16} />
          </span>
          <div>{I18n.t('send_notification')}</div>
        </UnstyledButton>

        <UnstyledButton
          aria-label={I18n.t('end_session')}
          className={cn(styles.settingsMenuRow, teacherPacedMode ? styles.teacherPacedUpdates : styles.legacy)}
          onClick={openEndSessionModal}
        >
          <span className={styles.menuIconButton}>
            <i className="fa fa-stop tw-text-base" />
          </span>
          <div>{I18n.t('end_session')}</div>
        </UnstyledButton>

        {isFetched && notifyModal.isOpen && (
          <NotifyStudentsModal
            alreadyJoinedStudentIds={connectedStudentIds}
            close={closeNotifyModal}
            isError={notifyMutation.isError}
            isOpen={notifyModal.isOpen}
            isSubmitting={notifyMutation.isLoading}
            programId={programId}
            stafferId={stafferId}
            submitText={I18n.t('send_notification')}
            submittingText={I18n.t('sending_notification')}
            onSubmit={handleRenotifySubmit}
          />
        )}

        {endSessionModalOpen && (
          <EndSessionModal
            endMutation={endMutation}
            modalManager={{
              close: closeEndSessionModal,
              isOpen: endSessionModalOpen,
              open: openEndSessionModal,
            }}
          />
        )}
      </>
    );
  }

  if (TPM_HIDDEN_ACTIVITY_TYPES.includes(activityType)) return null;

  // PLAT-16760 — only opted-in teachers can start a new session. An already-active session above
  // is unaffected, so opting out never interrupts a session in progress.
  if (!usesTeacherPacedMode) return null;

  return (
    <>
      <UnstyledButton
        aria-label={I18n.t('start_teacher_paced_mode')}
        className={cn(styles.settingsMenuRow, teacherPacedMode ? styles.teacherPacedUpdates : styles.legacy)}
        onClick={handleStartClick}
      >
        <span className={styles.menuIconButton}>
          <Icon icon="Live" size={16} />
        </span>
        <div>{I18n.t('start_teacher_paced_mode')}</div>
      </UnstyledButton>

      {isFetched && notifyModal.isOpen && (
        <NotifyStudentsModal
          alreadyJoinedStudentIds={connectedStudentIds}
          close={closeNotifyModal}
          isError={startMutation.isError || notifyMutation.isError}
          isOpen={notifyModal.isOpen}
          isSubmitting={startMutation.isLoading || notifyMutation.isLoading}
          programId={programId}
          stafferId={stafferId}
          onSubmit={handleInitialSubmit}
        />
      )}

      <ConfirmSessionEndModal modalManager={confirmSessionEndModal} />
      <SessionConflictModal modalManager={conflictModal} />
    </>
  );
};

export default LiveSessionControl;
